// 为主登录会话提供共享刷新请求与一次性 401 重试，避免刷新接口递归调用。
import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "@/enums/CacheEnum";

// 请求标记随 Axios 重试配置传递，免鉴权标记不能因删除请求头而丢失。
type AuthRequestConfig = InternalAxiosRequestConfig & {
  // 显式免鉴权请求不参与刷新和登录失效处理。
  __skipTokenRefresh?: boolean;
  // 每个业务请求只允许因 401 重试一次。
  __retriedAfterRefresh?: boolean;
};

// 两套业务实例共享同一个刷新 Promise。
let inflightRefresh: Promise<boolean> | null = null;

/**
 * 为免鉴权请求保留标记，并清除 no-auth 字面量请求头。
 * @param config 当前请求配置
 * @returns 是否应跳过会话刷新
 */
export function shouldSkipTokenRefresh(config: AuthRequestConfig): boolean {
  // 按完整路径末尾匹配，避免把含 login 字样的普通业务接口误判为登录。
  const path = (config.url || "").split(/[?#]/)[0];
  // no-auth 仅供前端标记，不发送给后端。
  const noAuth =
    String(config.headers.get("Authorization") || "").toLowerCase() ===
    "no-auth";
  if (noAuth) {
    config.__skipTokenRefresh = true;
    config.headers.delete("Authorization");
  }
  return (
    Boolean(config.__skipTokenRefresh) ||
    /\/(?:login|auth\/refresh|auth\/captcha|loginVerifyCodeApply|checkLoginVerifyCode)\/?$/.test(
      path
    )
  );
}

/**
 * 请求新的令牌，仅在原会话仍有效时保存，防止退出后的慢响应恢复会话。
 * @returns 是否获得并保存新的访问令牌
 */
async function requestRefreshTokens(): Promise<boolean> {
  // 捕获会话凭证，用于刷新完成后校验期间是否发生退出或重新登录。
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  // 刷新期间访问令牌变化意味着会话已被其他操作替换。
  const previousToken = localStorage.getItem(TOKEN_KEY);
  if (!refreshToken) return false;
  try {
    // 地址已包含 /api，直接走同源代理，避免与 baseURL 拼成 /api/api/v1。
    const response = await axios.post(
      "/api/v1/auth/refresh",
      { refreshToken },
      {
        timeout: 50_000,
        headers: { "Content-Type": "application/json;charset=utf-8" },
      }
    );
    // 裸 Axios 保留完整业务响应，成功码由刷新模块校验。
    const payload = response.data;
    // 当前后端以 token 字段返回访问令牌。
    const token = payload?.data?.token;
    if (payload?.code !== 0 || typeof token !== "string" || !token.trim())
      return false;
    if (
      localStorage.getItem(REFRESH_TOKEN_KEY) !== refreshToken ||
      localStorage.getItem(TOKEN_KEY) !== previousToken
    )
      return false;
    localStorage.setItem(TOKEN_KEY, token);
    // 后端未轮换刷新凭证时继续使用当前凭证。
    const nextRefreshToken = payload.data.refreshToken;
    localStorage.setItem(
      REFRESH_TOKEN_KEY,
      typeof nextRefreshToken === "string" && nextRefreshToken
        ? nextRefreshToken
        : refreshToken
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * 合并并发刷新调用。
 * @returns 本次共享刷新是否成功
 */
export function refreshAccessToken(): Promise<boolean> {
  if (!inflightRefresh) {
    inflightRefresh = requestRefreshTokens().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

/**
 * 401 后刷新并通过原实例重试，保留各实例的响应解包方式。
 * @param http 原业务请求实例
 * @param config 失败请求配置
 * @param onExpired 刷新失败或重试仍失效时的处理函数
 * @returns 重试请求的业务结果
 */
export async function retryAfterTokenRefresh(
  http: AxiosInstance,
  config: AuthRequestConfig,
  onExpired: () => void | Promise<void>
): Promise<AxiosResponse> {
  if (!config.__retriedAfterRefresh) {
    config.__retriedAfterRefresh = true;
    // 较晚返回的旧 Token 401 可直接复用其他请求已刷新的 Token。
    // 当前缓存可能已由更早返回的 401 请求更新。
    const currentToken = localStorage.getItem(TOKEN_KEY);
    // 去除协议前缀后比较实际凭证，避免大小写导致重复刷新。
    const sentToken = String(config.headers.get("Authorization") || "").replace(
      /^bearer\s+/i,
      ""
    );
    // 登录存储兼容带 bearer 前缀的令牌。
    const normalizedToken = (currentToken || "").replace(/^bearer\s+/i, "");
    if (
      (normalizedToken && normalizedToken !== sentToken) ||
      (await refreshAccessToken())
    ) {
      // 重试始终读取最新访问令牌。
      const token = localStorage.getItem(TOKEN_KEY) || "";
      config.headers.set(
        "Authorization",
        /^bearer\s/i.test(token) ? token : `bearer ${token}`
      );
      return http.request(config);
    }
  }
  await onExpired();
  throw new Error("登录已过期，请重新登录");
}
